import { useState, useEffect, useMemo } from "react";
import { SettingsFormTypes } from "models/formTypes";
import { ScheduledChangesInfo } from "./ScheduledChanges";

interface UseScheduledChangesProps {
  currentValues?: SettingsFormTypes.EmployeeSettings;
  isWintSalarySettingsAvailable?: boolean;
}

interface UseScheduledChangesReturn {
  scheduledChanges: ScheduledChangesInfo;
  viewMode: "current" | "scheduled";
  setViewMode: (mode: "current" | "scheduled") => void;
  displayValues: SettingsFormTypes.EmployeeSettings | undefined;
}

export const useScheduledChanges = ({
  currentValues,
  isWintSalarySettingsAvailable,
}: UseScheduledChangesProps): UseScheduledChangesReturn => {
  const [viewMode, setViewMode] = useState<"current" | "scheduled">("current");

  // Detect if there are scheduled changes
  const scheduledChanges = useMemo((): ScheduledChangesInfo => {
    if (!currentValues) {
      return { hasScheduledChanges: false, scheduledFields: [] };
    }

    // Only show scheduled changes info panel for WintSalary systems when there's a future schedule
    if (isWintSalarySettingsAvailable && currentValues.wintSalarySpecificSettings?.schedule?.futureSchedule) {
      const futureScheduleDate = currentValues.wintSalarySpecificSettings.schedule.futureSchedule.validFrom;
      if (futureScheduleDate) {
        try {
          const dateObj = new Date(futureScheduleDate as any);
          // Only use if it's a valid date
          if (!isNaN(dateObj.getTime())) {
            return {
              hasScheduledChanges: true,
              effectiveDate: dateObj,
              scheduledFields: ["schedule"],
            };
          }
        } catch (e) {
          // Invalid date, skip
        }
      }
    }

    return {
      hasScheduledChanges: false,
      scheduledFields: [],
    };
  }, [currentValues, isWintSalarySettingsAvailable]);

  // Reset view mode when scheduled changes disappear
  useEffect(() => {
    if (!scheduledChanges.hasScheduledChanges && viewMode === "scheduled") {
      setViewMode("current");
    }
  }, [scheduledChanges.hasScheduledChanges, viewMode]);

  // Get display values based on view mode
  const displayValues = useMemo((): SettingsFormTypes.EmployeeSettings | undefined => {
    if (!currentValues || viewMode === "current") {
      return currentValues;
    }

    const scheduledValues = { ...currentValues };
    if (scheduledChanges.hasScheduledChanges && viewMode === "scheduled") {
      if (isWintSalarySettingsAvailable && currentValues.wintSalarySpecificSettings?.schedule?.futureSchedule) {
        scheduledValues.schedule = currentValues.wintSalarySpecificSettings.schedule.futureSchedule.scheduleWeeks;
        scheduledValues.employmentRate = currentValues.wintSalarySpecificSettings.schedule.futureSchedule.workRate;
        scheduledValues.usualWorkHours =
          currentValues.wintSalarySpecificSettings.schedule.futureSchedule.standardWorkTime;
      }
    }

    return scheduledValues;
  }, [currentValues, viewMode, scheduledChanges, isWintSalarySettingsAvailable]);

  return {
    scheduledChanges,
    viewMode,
    setViewMode,
    displayValues,
  };
};

export default useScheduledChanges;
